/**
 * Settings Page
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePreferencesStore } from '@/store/usePreferencesStore';

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const theme = usePreferencesStore((s) => s.theme);
  const setTheme = usePreferencesStore((s) => s.setTheme);
  const fontSize = usePreferencesStore((s) => s.fontSize);
  const setFontSize = usePreferencesStore((s) => s.setFontSize);
  const autoRunPipeline = usePreferencesStore((s) => s.autoRunPipeline);
  const setAutoRunPipeline = usePreferencesStore((s) => s.setAutoRunPipeline);
  const autoCopyOutputToClipboard = usePreferencesStore((s) => s.autoCopyOutputToClipboard);
  const setAutoCopyOutputToClipboard = usePreferencesStore((s) => s.setAutoCopyOutputToClipboard);
  const showIoStats = usePreferencesStore((s) => s.showIoStats);
  const setShowIoStats = usePreferencesStore((s) => s.setShowIoStats);
  const showToolNameWithStepLabel = usePreferencesStore((s) => s.showToolNameWithStepLabel);
  const setShowToolNameWithStepLabel = usePreferencesStore((s) => s.setShowToolNameWithStepLabel);
  const autoVisualizers = usePreferencesStore((s) => s.autoVisualizers);
  const setAutoVisualizers = usePreferencesStore((s) => s.setAutoVisualizers);
  const showVisualizersInPipeline = usePreferencesStore((s) => s.showVisualizersInPipeline);
  const setShowVisualizersInPipeline = usePreferencesStore((s) => s.setShowVisualizersInPipeline);
  const resetToDefaults = usePreferencesStore((s) => s.resetToDefaults);

  return (
    <div className="h-full overflow-auto">
      <div className="p-8 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">{t('breadcrumb')}</div>
            <h1 className="text-3xl font-semibold mt-1">{t('title')}</h1>
          </div>
        </div>

        <div className="mt-8 border border-border rounded-lg p-6 bg-muted/10">
          <div className="text-lg font-semibold">{t('sections.appearance')}</div>
          <div className="mt-4 space-y-4">
            <SettingRow
              title={t('theme.label')}
              description={t('theme.description')}
              control={
                <div className="w-64">
                  <Select
                    value={theme}
                    onValueChange={(val) => setTheme(val as 'light' | 'dark' | 'system')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('theme.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">{t('theme.system')}</SelectItem>
                      <SelectItem value="dark">{t('theme.dark')}</SelectItem>
                      <SelectItem value="light">{t('theme.light')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              }
            />
            <SettingRow
              title={t('fontSize.label')}
              description={t('fontSize.description')}
              control={
                <div className="w-64">
                  <Select
                    value={fontSize}
                    onValueChange={(val) => setFontSize(val as 'small' | 'medium' | 'large')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('fontSize.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">{t('fontSize.small')}</SelectItem>
                      <SelectItem value="medium">{t('fontSize.medium')}</SelectItem>
                      <SelectItem value="large">{t('fontSize.large')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              }
            />
          </div>

          <div className="mt-8 text-lg font-semibold">{t('sections.automation')}</div>
          <div className="mt-4 space-y-4">
            <SettingRow
              title={t('autoRun.label')}
              description={t('autoRun.description')}
              control={<Switch checked={autoRunPipeline} onCheckedChange={setAutoRunPipeline} />}
            />
            <SettingRow
              title={t('autoClipboard.label')}
              description={t('autoClipboard.description')}
              control={
                <Switch
                  checked={autoCopyOutputToClipboard}
                  onCheckedChange={setAutoCopyOutputToClipboard}
                />
              }
            />
          </div>

          <div className="mt-8 text-lg font-semibold">{t('sections.editor')}</div>
          <div className="mt-4 space-y-4">
            <SettingRow
              title={t('autoVisualizers.label')}
              description={t('autoVisualizers.description')}
              control={<Switch checked={autoVisualizers} onCheckedChange={setAutoVisualizers} />}
            />
            <SettingRow
              title={t('ioStats.label')}
              description={t('ioStats.description')}
              control={<Switch checked={showIoStats} onCheckedChange={setShowIoStats} />}
            />
            <SettingRow
              title={t('toolNameWithLabel.label')}
              description={t('toolNameWithLabel.description')}
              control={
                <Switch
                  checked={showToolNameWithStepLabel}
                  onCheckedChange={setShowToolNameWithStepLabel}
                />
              }
            />
            <SettingRow
              title={t('showVisualizersInPipeline.label')}
              description={t('showVisualizersInPipeline.description')}
              control={
                <Switch
                  checked={showVisualizersInPipeline}
                  onCheckedChange={setShowVisualizersInPipeline}
                />
              }
            />
          </div>

          <div className="mt-8 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={resetToDefaults}>
              {t('actions.resetToDefaults')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingRow: React.FC<{
  title: string;
  description: string;
  control: React.ReactNode;
}> = ({ title, description, control }) => {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
};
